// SCSS Imports
import "./MainNavigation.scss";

// React Imports
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import CognitoData from "./CognitoData";

// Image Imports
import logo from "../images/brainbased-logo-green-1.png";
import smallLogo from "../images/brainbased-logo-green-small.png";

//Font Awesome Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserCircle,
    faBars,
    faCog,
    faUser,
    faWindowClose,
    faHome,
    faAngleDoubleLeft,
    faAngleDoubleRight,
    faChalkboardTeacher,
    faVideo,
    faSearch,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";

import { Auth } from "aws-amplify";
import { faRev } from "@fortawesome/free-brands-svg-icons";

const Navigation = ({ isOpen, handleToggle, onSignOut, searchQuery, onSearchChange }) => {
    const [isAccountDropdownOpen, setAccountDropdownOpen] = useState(false);
    const [isMainMenuOpen, setMainMenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigationBarRef = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const user = CognitoData(); // Fetching user data from CognitoData component
    const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(isOpen);
    const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
    const location = useLocation();
    
    // Mobile detection
    const isMobile = viewportWidth <= 720;
    
    // Show search only on video library pages
    const showSearch = ['/', '/home'].includes(location.pathname) 
        || location.pathname.startsWith('/play')
        || location.pathname.startsWith('/home/');
    
    const handleMobileSearchToggle = () => {
        setIsMobileSearchExpanded(!isMobileSearchExpanded);
    };
    
    const handleMobileSearchClose = () => {
        setIsMobileSearchExpanded(false);
    };
    const toggleFilterSidebar = () => {
        const newOpenState = !isFilterSidebarOpen;
        setIsFilterSidebarOpen(newOpenState);
        handleToggle(newOpenState);
    };
    const handleSignOut = async () => {
        try {
            await Auth.signOut();
            onSignOut(); // Call the callback passed from App.js
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        setIsFilterSidebarOpen(isOpen);
    }, [isOpen]);

    const toggleAccountDropdown = () => {
        setAccountDropdownOpen(!isAccountDropdownOpen);
        if (isMainMenuOpen) {
            setMainMenuOpen(false);
        }
    };

    const toggleMainMenu = () => {
        setMainMenuOpen(!isMainMenuOpen);
        if (isAccountDropdownOpen) {
            setAccountDropdownOpen(false);
        }
    };

    const closeDropdowns = () => {
        setAccountDropdownOpen(false);
        setMainMenuOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                closeDropdowns();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const calculateRightAttribute = () => {
        if (navigationBarRef.current) {
            // Replace 'otherComponentWidth' with the actual width of the other component
            const navigationBarWidth = navigationBarRef.current.offsetWidth;

            // Calculate the value for the 'right' CSS attribute
            const rightValue = (viewportWidth - navigationBarWidth) / 2;

            return `${rightValue}px`;
        }
        return "auto";
    };

    const redirectHome = () => {
        let idToken = null;
        for (var i = 0; i < localStorage.length; i++) {
            var key = localStorage.key(i);
            if (/CognitoIdentityServiceProvider\..*\.idToken/.test(key)) {
                idToken = localStorage.getItem(key);
                //console.log(idToken);
                break; // Break the loop once token found
            }
        }
        if (idToken) {
            // Construct the URL with both idToken and pool parameters
            const redirectUrl = `https://portal.brainbased.com/cognito-intermediate/#id_token=${encodeURIComponent(
                idToken
            )}&pool=pool1`;
            window.open(redirectUrl, '_blank');
        } else {
            console.error("idToken not found in localStorage");
        }
    };

    return (
        <div className="navigation-container">
            <nav ref={navigationBarRef} className={`navigation-bar ${isMobile && isMobileSearchExpanded ? 'search-expanded' : ''}`}>
                <Link to="/" className="logo-link">
                    <img
                        className="logo"
                        src={viewportWidth <= 1060 ? smallLogo : logo}
                        alt="Logo"
                    />
                </Link>
                
                {showSearch && (
                    isMobile ? (
                        // Mobile: icon-only or expanded search
                        isMobileSearchExpanded ? (
                            <div className="search-container mobile-expanded">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="search"
                                    placeholder="Search videos..."
                                    value={searchQuery || ""}
                                    onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                                    className="search-input"
                                    autoFocus
                                />
                                <button 
                                    className="search-clear-button"
                                    onClick={handleMobileSearchClose}
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        ) : (
                            <button 
                                className="search-icon-button"
                                onClick={handleMobileSearchToggle}
                                type="button"
                            >
                                <FontAwesomeIcon icon={faSearch} />
                            </button>
                        )
                    ) : (
                        // Desktop: full search bar
                        <div className="search-container">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="search"
                                placeholder="Search videos..."
                                value={searchQuery || ""}
                                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                                className="search-input"
                            />
                            {searchQuery && (
                                <button 
                                    className="search-clear-button"
                                    onClick={() => onSearchChange && onSearchChange('')}
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    )
                )}
                
                <div className="links">
                    {/* New Live Classes Link */}
                    <Link
                        className="dropdown-content-link"
                        to="/LiveClasses"
                        >
                        <FontAwesomeIcon
                            className="dropdown-content-link-icon"
                            icon={faChalkboardTeacher}
                        />
                        Live Classes
                    </Link>
                    {/*<Link
                        className="dropdown-content-link"
                        to="/"
                        onClick={redirectHome}
                        >
                        <FontAwesomeIcon
                            className="dropdown-content-link-icon"
                            icon={faChalkboardTeacher}
                        />
                        Live Classes
                    </Link>/
                    {/* SAVED FOR LATER 
                    <Link to="/"></Link>
                    <Link to="/"></Link>
                    <Link to="/"></Link>
                    */}
                </div>
                <div className="dropdowns" ref={dropdownRef}>
                    <div
                        className={`dropdown account-dropdown ${
                            isAccountDropdownOpen ? "active" : ""
                        }`}
                    >
                        <button
                            /* UNCOMMENT WHEN USED AS A DROPDOWN
                            className="dropdown-button"
                            onClick={toggleAccountDropdown}
                            */
                            className="account-button"
                        >
                            <span className="dropdown-button-text">
                                {user ? user.username : "Loading..."}
                            </span>

                            <FontAwesomeIcon
                                className="dropdown-icon"
                                icon={faUserCircle}
                            />
                        </button>
                        <div
                            className="dropdown-content"
                            style={{ right: calculateRightAttribute() }}
                        >
                            <Link
                                className="dropdown-content-link"
                                to="/UserAccount"
                            >
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={faUser}
                                />
                                Account
                            </Link>
                            <Link className="dropdown-content-link" to="">
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={faCog}
                                />
                                Account Settings
                            </Link>
                        </div>
                    </div>
                    <div
                        className={`dropdown main-menu ${
                            isMainMenuOpen ? "active" : ""
                        }`}
                    >
                        <button
                            className="dropdown-button"
                            onClick={toggleMainMenu}
                        >
                            <FontAwesomeIcon
                                className="dropdown-icon"
                                icon={faBars}
                            />
                        </button>
                        <div
                            className="dropdown-content"
                            style={{ right: calculateRightAttribute() }}
                        >
                            <Link
                                className="dropdown-content-link"
                                onClick={toggleFilterSidebar}
                            >
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={
                                        isFilterSidebarOpen
                                            ? faAngleDoubleLeft
                                            : faAngleDoubleRight
                                    }
                                />
                                {isFilterSidebarOpen
                                    ? "Hide Filters"
                                    : "Show Filters"}
                                {/* May change to Library with new icon */}
                            </Link>
                            <Link
                                className="dropdown-content-link"
                                to="/" 
                            >
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={faVideo}
                                />
                                Video Library
                            </Link>
                            <Link
                                className="dropdown-content-link"
                                to="/LiveClasses"
                            >
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={faChalkboardTeacher}
                                />
                                Live Classes
                            </Link>
                            <Link
                                className="dropdown-content-link"
                                to="/"
                                onClick={redirectHome}
                            >
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={faHome}
                                />
                                My Account
                            </Link>
                            <Link
                                className="dropdown-content-link"
                                onClick={handleSignOut}
                            >
                                <FontAwesomeIcon
                                    className="dropdown-content-link-icon"
                                    icon={faWindowClose}
                                />
                                Log Out
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    );
};

export default Navigation;
